import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react';
import { useEffect, useRef, useState, type AnimationEvent, type CSSProperties, type ComponentType } from 'react';
import type { ToastItem, ToastVariant } from './types';
import styles from './ToastStack.module.css';

type ToastStackProps = {
  items: ToastItem[];
  onDismiss: (id: number) => void;
};

const ICONE: Record<ToastVariant, ComponentType<{ size?: number; className?: string }>> = {
  success: CircleCheck,
  error: CircleX,
  warning: TriangleAlert,
  info: Info,
};

const ROTULO: Record<ToastVariant, string> = {
  success: 'Sucesso',
  error: 'Erro',
  warning: 'Aviso',
  info: 'Informação',
};

function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [saindo, setSaindo] = useState(false);
  const restanteRef = useRef(item.durationMs);
  const ultimoTickRef = useRef(0);
  const timerRef = useRef<number>(undefined);
  const Icone = ICONE[item.variant];
  const urgente = item.variant === 'error' || item.variant === 'warning';

  const fechar = () => setSaindo(true);

  useEffect(() => {
    if (item.durationMs == null) return;
    ultimoTickRef.current = Date.now();
    restanteRef.current = item.durationMs;
    timerRef.current = window.setTimeout(fechar, item.durationMs);
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [item.durationMs]);

  useEffect(() => {
    if (!saindo) return;
    if (!prefersReducedMotion()) return;
    onDismiss(item.id);
  }, [saindo, item.id, onDismiss]);

  const pausar = () => {
    if (item.durationMs == null || restanteRef.current == null) return;
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    restanteRef.current -= Date.now() - ultimoTickRef.current;
  };

  const retomar = () => {
    if (item.durationMs == null || restanteRef.current == null || saindo) return;
    ultimoTickRef.current = Date.now();
    timerRef.current = window.setTimeout(fechar, Math.max(0, restanteRef.current));
  };

  const onAnimacao = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (saindo) onDismiss(item.id);
  };

  const duracaoStyle =
    item.durationMs != null
      ? ({ '--toast-duration': `${item.durationMs}ms` } as CSSProperties)
      : undefined;

  const abrir = () => {
    item.onClick?.();
    fechar();
  };

  const conteudo = (
    <>
      <span className={styles.iconWrap} aria-hidden>
        <Icone size={18} className={styles.icone} />
      </span>
      <div className={styles.body}>
        <strong className={styles.rotulo}>{ROTULO[item.variant]}</strong>
        <p className={styles.mensagem}>{item.message}</p>
      </div>
    </>
  );

  return (
    <div
      className={`${styles.toast} ${styles[item.variant]} ${saindo ? styles.saindo : ''}`}
      role={urgente ? 'alert' : 'status'}
      style={duracaoStyle}
      onAnimationEnd={onAnimacao}
      onMouseEnter={pausar}
      onMouseLeave={retomar}
    >
      {item.onClick ? (
        <button type="button" className={styles.abrir} onClick={abrir}>
          {conteudo}
        </button>
      ) : (
        conteudo
      )}
      <button type="button" className={styles.dismiss} onClick={fechar} aria-label="Fechar">
        <X size={14} />
      </button>
      {item.durationMs != null ? <span className={styles.progresso} /> : null}
    </div>
  );
}

export function ToastStack({ items, onDismiss }: ToastStackProps) {
  if (items.length === 0) return null;

  return (
    <div className={styles.stack} aria-live="polite">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
